/**
 * LeadsManager Component
 * Displays contact form leads for the owner dashboard
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ownerAPI } from '../services/api';
import LeadsConversion from './LeadsConversion';
import './LeadsManager.css';

/**
 * Der Weg einer Anfrage. `offen: true` heisst: noch kein Ergebnis, es wird
 * weiter erinnert. Nur Gewonnen und Verloren beenden das - ein Anrufversuch
 * ist ausdruecklich kein Ergebnis.
 *
 * `legacy` markiert Werte aus der Zeit vor dem Nachfass-Workflow. Sie stehen
 * nur noch im Auswahlfeld, damit ein alter Datensatz nicht seinen eigenen
 * Status verliert, tauchen aber nicht mehr als Filter auf.
 */
const STATUS_OPTIONS = [
  { value: 'new', label: 'Neu', color: '#3b82f6', offen: true },
  { value: 'attempted', label: 'Kontaktversuch', color: '#f59e0b', offen: true },
  { value: 'appointment', label: 'Termin vereinbart', color: '#8b5cf6', offen: true },
  { value: 'trial_done', label: 'Probetraining war', color: '#0ea5e9', offen: true },
  { value: 'converted', label: 'Mitgliedschaft gewonnen', color: '#22c55e', offen: false },
  { value: 'pt_martin', label: 'PT Martin gewonnen', color: '#16a34a', offen: false, pt: true },
  { value: 'pt_betti', label: 'PT Betti gewonnen', color: '#16a34a', offen: false, pt: true },
  { value: 'pt_nico', label: 'PT Nico gewonnen', color: '#16a34a', offen: false, pt: true },
  { value: 'lost', label: 'Verloren', color: '#94a3b8', offen: false },
  { value: 'contacted', label: 'Kontaktiert (alt)', color: '#f59e0b', offen: true, legacy: true },
  { value: 'closed', label: 'Geschlossen (alt)', color: '#94a3b8', offen: false, legacy: true },
];

/**
 * Die Auswahl im Kontaktformular der Website (website/kontakt.html und
 * website/index.html). Bei Aenderungen dort hier nachziehen, damit telefonisch
 * erfasste Anfragen dieselben Interessen tragen koennen.
 */
const INTERESSEN = [
  '1:1 Personal Training',
  'Gesundheitscheck & Anamnese',
  'Mobilität & Beweglichkeit',
  'Stabilität & Koordination',
  'Kraft & Muskelerhalt 40+',
  'Schmerzfrei(er) bewegen',
  'Reha-Begleitung',
  'Box-Training / Kickboxen',
  'Woman-Only',
];

const LEERER_ANRUF = { name: '', phone: '', email: '', studio: '', interests: [], message: '', assigned_to: '' };

/**
 * Anruf als Anfrage erfassen - mit genau den Feldern des Website-Formulars,
 * damit die Anfrage danach wie jede andere behandelt wird.
 */
const AnrufErfassen = ({ zustaendige, onGespeichert, onAbbrechen, showMessage }) => {
  const [werte, setWerte] = useState(LEERER_ANRUF);
  const [speichert, setSpeichert] = useState(false);

  const setze = (feld) => (e) => setWerte(prev => ({ ...prev, [feld]: e.target.value }));

  const umschalten = (interesse) => setWerte(prev => ({
    ...prev,
    interests: prev.interests.includes(interesse)
      ? prev.interests.filter(i => i !== interesse)
      : [...prev.interests, interesse],
  }));

  const absenden = async (e) => {
    e.preventDefault();
    if (!werte.name.trim() || !werte.phone.trim()) {
      showMessage('Name und Telefonnummer sind erforderlich', 'error');
      return;
    }
    setSpeichert(true);
    try {
      // Reihenfolge der Interessen wie auf der Website, nicht wie angeklickt
      const interests = INTERESSEN.filter(i => werte.interests.includes(i));
      const { data } = await ownerAPI.createLead({ ...werte, interests });
      onGespeichert(data);
    } catch (err) {
      showMessage(err.response?.data?.error || 'Anfrage konnte nicht gespeichert werden', 'error');
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <form className="lead-anruf-form" onSubmit={absenden}>
      <h3>Telefonische Anfrage erfassen</h3>
      <div className="lead-anruf-row">
        <label>
          Name *
          <input type="text" value={werte.name} onChange={setze('name')} required autoFocus />
        </label>
        <label>
          Telefon *
          <input type="tel" value={werte.phone} onChange={setze('phone')} required />
        </label>
      </div>
      <div className="lead-anruf-row">
        <label>
          E-Mail
          <input type="email" value={werte.email} onChange={setze('email')} />
        </label>
        <label>
          Bevorzugter Standort
          <select value={werte.studio} onChange={setze('studio')}>
            <option value="">Keine Angabe</option>
            <option value="Klosterneuburg">Klosterneuburg</option>
            <option value="Weidling">Weidling</option>
          </select>
        </label>
      </div>
      <fieldset className="lead-anruf-interessen">
        <legend>Interesse an Personal Training</legend>
        {INTERESSEN.map(i => (
          <label key={i} className="lead-anruf-checkbox">
            <input type="checkbox" checked={werte.interests.includes(i)} onChange={() => umschalten(i)} />
            {i}
          </label>
        ))}
      </fieldset>
      <label>
        Nachricht / Gesprächsnotiz
        <textarea rows="3" value={werte.message} onChange={setze('message')} placeholder="Was sind die Ziele?" />
      </label>
      {zustaendige.length > 0 && (
        <label className="lead-anruf-zustaendig">
          Zuständig
          <select value={werte.assigned_to} onChange={setze('assigned_to')}>
            <option value="">Niemandem zugeteilt</option>
            {zustaendige.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
      )}
      <div className="lead-anruf-actions">
        <button type="submit" className="leads-btn" disabled={speichert}>
          {speichert ? 'Speichere…' : 'Anfrage speichern'}
        </button>
        <button type="button" className="leads-btn leads-btn-outline" onClick={onAbbrechen}>
          Abbrechen
        </button>
      </div>
    </form>
  );
};

/** Filterzeile: nach Arbeitsstand, nicht nach jedem Einzelstatus. */
const FILTER_OPTIONS = [
  { value: 'offen', label: 'Offen' },
  { value: 'all', label: 'Alle' },
  { value: 'gewonnen', label: 'Gewonnen' },
  { value: 'lost', label: 'Verloren' },
];

/** Mitgliedschaft oder Personal Training - beides ein Abschluss. */
const istGewonnen = (status) => status === 'converted' || String(status).startsWith('pt_');

const istOffenerStatus = (status) => {
  const def = STATUS_OPTIONS.find(o => o.value === status);
  return def ? def.offen : true;
};

/**
 * Zeigt, ob aus einer Anfrage ein Mitglied geworden ist.
 *
 * Der Abgleich laeuft ueber die E-Mail-Adresse und kann deshalb nur EINE
 * Richtung beweisen: Ein Treffer heisst sicher Mitglied, ein fehlender Treffer
 * heisst nur "unter dieser Adresse nicht gefunden". Wer sich spaeter mit einer
 * anderen Adresse angemeldet hat, taucht hier nicht auf - deshalb steht da
 * "kein Treffer" und nicht "kein Mitglied".
 */
const MitgliedBadge = ({ lead }) => {
  if (!lead.email) return <span className="lead-member-badge unknown">E-Mail fehlt</span>;
  if (!lead.mitglied) {
    return (
      <span
        className="lead-member-badge none"
        title={'Unter dieser E-Mail-Adresse gibt es keinen Kundendatensatz. Wer sich mit einer anderen Adresse angemeldet hat, wird hier nicht erkannt – dann den Status von Hand auf „Gewonnen“ setzen.'}
      >
        kein Treffer
      </span>
    );
  }

  const { aktiv, status, name } = lead.mitglied;
  return (
    <span
      className={`lead-member-badge ${aktiv ? 'member' : 'former'}`}
      title={`Kundendatensatz: ${name} (${status})`}
    >
      {aktiv ? 'Mitglied' : `Mitglied (${status})`}
    </span>
  );
};

const LeadsManager = ({ showMessage }) => {
  const [leads, setLeads] = useState([]);
  const [spamLeads, setSpamLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('offen');
  const [editingNotes, setEditingNotes] = useState(null);
  const [erinnerung, setErinnerung] = useState(null);
  const [zustaendige, setZustaendige] = useState([]);
  const [filterPerson, setFilterPerson] = useState('');
  const [notesText, setNotesText] = useState('');
  const [anrufOffen, setAnrufOffen] = useState(false);

  // Beide Toepfe einmal laden und danach im Browser filtern. Das haelt die
  // Zaehler in den Filterknoepfen richtig - vorher zeigten sie immer nur die
  // gerade geladene Teilmenge - und der Filterwechsel braucht keinen Roundtrip.
  const fetchLeads = useCallback(async () => {
    try {
      const [echte, spam, personen] = await Promise.all([
        ownerAPI.getLeads(),
        ownerAPI.getLeads({ status: 'spam' }),
        ownerAPI.getLeadZustaendige(),
      ]);
      setLeads(echte.data);
      setSpamLeads(spam.data);
      setZustaendige(personen.data.zustaendige || []);
    } catch (err) {
      showMessage('Fehler beim Laden der Anfragen', 'error');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await ownerAPI.updateLead(id, { status: newStatus });
      const anpassen = prev => prev.map(l => (l.id === id ? { ...l, status: newStatus } : l));
      setLeads(anpassen);
      setSpamLeads(anpassen);
      // Ohne Rueckmeldung wirkt es wie ein Fehler: die Karte faellt aus dem
      // gerade aktiven Filter und verschwindet einfach.
      const label = (STATUS_OPTIONS.find(o => o.value === newStatus) || {}).label || newStatus;
      showMessage(`Status geändert: ${label}`, 'success');
    } catch (err) {
      const meldung = err.response?.data?.error || 'Status konnte nicht geändert werden';
      showMessage(meldung, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Anfrage wirklich löschen?')) return;
    try {
      await ownerAPI.deleteLead(id);
      setLeads(prev => prev.filter(l => l.id !== id));
      setSpamLeads(prev => prev.filter(l => l.id !== id));
      showMessage('Anfrage gelöscht', 'success');
    } catch (err) {
      showMessage('Anfrage konnte nicht gelöscht werden', 'error');
    }
  };

  /**
   * Holt eine faelschlich aussortierte Anfrage zurueck.
   *
   * Erkennungsmerkmal ist der Vermerk im notes-Feld (siehe spam.service.js).
   * Wird er entfernt, gilt die Anfrage wieder als regulaer - deshalb reicht es
   * nicht, nur den Status umzustellen.
   */
  const handleKeinSpam = async (lead) => {
    const bereinigt = (lead.notes || '').replace(/\s*\[Spam-Verdacht:[^\]]*\]/g, '').trim() || null;
    try {
      await ownerAPI.updateLead(lead.id, { notes: bereinigt, status: 'new' });
      const zurueck = { ...lead, notes: bereinigt, status: 'new' };
      setSpamLeads(prev => prev.filter(l => l.id !== lead.id));
      setLeads(prev => [zurueck, ...prev].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      showMessage('Anfrage als echt markiert', 'success');
    } catch (err) {
      showMessage('Anfrage konnte nicht zurückgeholt werden', 'error');
    }
  };

  /**
   * Schickt die Sammelmail sofort, statt bis zum naechsten Morgen zu warten.
   * Mit Rueckfrage, weil sie an alle Owner geht.
   */
  const handleErinnerung = async () => {
    if (!window.confirm(`Erinnerung mit ${offene.length} offenen Anfragen jetzt an beide Owner senden?`)) return;
    setErinnerung('laeuft');
    try {
      const { data } = await ownerAPI.sendeLeadErinnerung();
      showMessage(
        data.verschickt
          ? `Erinnerung verschickt an ${data.empfaenger.join(', ')}`
          : `Nichts verschickt: ${data.grund}`,
        data.verschickt ? 'success' : 'info'
      );
    } catch (err) {
      showMessage('Erinnerung konnte nicht verschickt werden', 'error');
    } finally {
      setErinnerung(null);
    }
  };

  /** Anfrage jemandem uebergeben - oder die Zuteilung wieder aufheben. */
  const handleZuteilung = async (id, person) => {
    try {
      await ownerAPI.updateLead(id, { assigned_to: person });
      setLeads(prev => prev.map(l => (l.id === id ? { ...l, assigned_to: person || null } : l)));
      showMessage(person ? `Zugeteilt an ${person}` : 'Zuteilung aufgehoben', 'success');
    } catch (err) {
      showMessage(err.response?.data?.error || 'Zuteilung fehlgeschlagen', 'error');
    }
  };

  const handleAnrufGespeichert = (lead) => {
    setLeads(prev => [{ ...lead, mitglied: null, nachfass: null }, ...prev]);
    setAnrufOffen(false);
    // Neue Anfragen sind offen - sonst waere die gerade erfasste nicht zu sehen
    setFilterStatus('offen');
    showMessage(`Anfrage von ${lead.name} erfasst`, 'success');
  };

  const handleSaveNotes = async (id) => {
    try {
      await ownerAPI.updateLead(id, { notes: notesText });
      const anpassen = prev => prev.map(l => (l.id === id ? { ...l, notes: notesText } : l));
      setLeads(anpassen);
      setSpamLeads(anpassen);
      setEditingNotes(null);
      showMessage('Notiz gespeichert', 'success');
    } catch (err) {
      showMessage('Notiz konnte nicht gespeichert werden', 'error');
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-AT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusInfo = (status) => STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];

  const offene = leads.filter(l => istOffenerStatus(l.status));
  const faellige = offene.filter(l => l.nachfass && l.nachfass.faellig);
  const dringende = faellige.filter(l => l.nachfass.dringend);

  const counts = {
    offen: offene.length,
    all: leads.length,
    gewonnen: leads.filter(l => istGewonnen(l.status)).length,
    lost: leads.filter(l => l.status === 'lost' || l.status === 'closed').length,
    spam: spamLeads.length
  };

  const nachStatus = filterStatus === 'spam'
    ? spamLeads
    : filterStatus === 'all'
      ? leads
      : filterStatus === 'offen'
        ? offene
        : filterStatus === 'gewonnen'
          ? leads.filter(l => istGewonnen(l.status))
          : filterStatus === 'lost'
            ? leads.filter(l => l.status === 'lost' || l.status === 'closed')
            : leads.filter(l => l.status === filterStatus);

  // Personenfilter: '-' steht fuer "noch niemandem zugeteilt"
  const sichtbar = !filterPerson
    ? nachStatus
    : filterPerson === '-'
      ? nachStatus.filter(l => !l.assigned_to)
      : nachStatus.filter(l => l.assigned_to === filterPerson);

  if (loading) return <div className="leads-loading">Lade Anfragen...</div>;

  return (
    <div className="leads-container">
      <div className="leads-header">
        <div>
          <h2>Kontaktanfragen</h2>
          <p className="leads-subtitle">
            {/* Die Conversion-Zahl steht in der Auswertung darunter - hier stuenden
                sonst zwei verschiedene Definitionen von "gewonnen" nebeneinander. */}
            Anfragen vom Kontaktformular der Website und telefonische Anfragen
            {counts.spam > 0 && <> · {counts.spam} als Spam aussortiert</>}
          </p>
        </div>
        {!anrufOffen && (
          <button className="leads-btn" onClick={() => setAnrufOffen(true)}>
            + Anruf erfassen
          </button>
        )}
      </div>

      {anrufOffen && (
        <AnrufErfassen
          zustaendige={zustaendige}
          onGespeichert={handleAnrufGespeichert}
          onAbbrechen={() => setAnrufOffen(false)}
          showMessage={showMessage}
        />
      )}

      {offene.length > 0 && (
        <div className={`leads-faellig ${dringende.length > 0 ? 'dringend' : ''}`}>
          <div className="leads-faellig-text">
            <strong>
              {offene.length} {offene.length === 1 ? 'offene Anfrage' : 'offene Anfragen'}
            </strong>
            {faellige.length > 0 && <> · {faellige.length} überfällig</>}
            {dringende.length > 0 && <> · {dringende.length} länger als eine Woche</>}
            <span className="leads-faellig-hinweis">
              Eine Anfrage ist erst erledigt, wenn sie auf Gewonnen, PT gewonnen oder Verloren steht.
              Bis dahin steht sie jeden Morgen in der Erinnerung an beide Owner.
            </span>
          </div>
          <div className="leads-faellig-aktionen">
            {filterStatus !== 'offen' && (
              <button className="leads-btn leads-btn-sm" onClick={() => setFilterStatus('offen')}>
                Offene zeigen
              </button>
            )}
            <button className="leads-btn leads-btn-sm leads-btn-outline" onClick={handleErinnerung} disabled={erinnerung === 'laeuft'}>
              {erinnerung === 'laeuft' ? 'Sende…' : 'Erinnerung jetzt senden'}
            </button>
          </div>
        </div>
      )}

      <LeadsConversion leads={leads} />

      <div className="leads-filters">
        {[
          ...FILTER_OPTIONS,
          { value: 'spam', label: 'Spam' }
        ].map(opt => (
          <button
            key={opt.value}
            className={`leads-filter-btn ${filterStatus === opt.value ? 'active' : ''} ${opt.value === 'spam' ? 'spam' : ''}`}
            onClick={() => setFilterStatus(opt.value)}
          >
            {opt.label}
            <span className="leads-filter-count">{counts[opt.value] || 0}</span>
          </button>
        ))}

        {zustaendige.length > 0 && filterStatus !== 'spam' && (
          <select
            className="leads-person-filter"
            value={filterPerson}
            onChange={(e) => setFilterPerson(e.target.value)}
            aria-label="Nach Zuständigkeit filtern"
          >
            <option value="">Alle Zuständigkeiten</option>
            <option value="-">Niemandem zugeteilt ({nachStatus.filter(l => !l.assigned_to).length})</option>
            {zustaendige.map(p => (
              <option key={p} value={p}>{p} ({nachStatus.filter(l => l.assigned_to === p).length})</option>
            ))}
          </select>
        )}
      </div>

      {filterStatus === 'lost' && sichtbar.length > 0 && (
        <p className="leads-spam-hinweis">
          Verloren ist keine Einbahnstraße. Der Abgleich mit den Mitgliedern läuft über die
          E-Mail-Adresse – wer sich später mit einer anderen Adresse angemeldet hat, steht
          hier zu Unrecht. Status einfach zurück auf „Gewonnen" stellen, dann stimmt auch
          die Auswertung wieder.
        </p>
      )}

      {filterStatus === 'spam' && spamLeads.length > 0 && (
        <p className="leads-spam-hinweis">
          Automatisch als Spam erkannt. Diese Anfragen lösen keine Benachrichtigung aus
          und zählen nicht in der Statistik. Liegt die Erkennung daneben, holt „Kein Spam"
          die Anfrage zurück in die reguläre Liste.
        </p>
      )}

      {sichtbar.length === 0 ? (
        <div className="leads-empty">
          {filterStatus === 'all'
            ? 'Noch keine Anfragen eingegangen.'
            : filterStatus === 'spam'
              ? 'Kein Spam vorhanden.'
              : filterStatus === 'offen'
                ? 'Nichts offen – bei jeder Anfrage steht, ob sie gewonnen oder verloren ist.'
                : `Keine Anfragen mit Status „${(FILTER_OPTIONS.find(o => o.value === filterStatus) || {}).label}".`}
        </div>
      ) : (
        <div className="leads-list">
          {sichtbar.map(lead => {
            const statusInfo = getStatusInfo(lead.status);
            return (
              <div key={lead.id} className={`lead-card lead-status-${lead.status}`}>
                <div className="lead-header">
                  <div className="lead-name-row">
                    <h3 className="lead-name">{lead.name}</h3>
                    {filterStatus !== 'spam' && <MitgliedBadge lead={lead} />}
                    {lead.nachfass && lead.nachfass.faellig && (
                      <span className={`lead-faellig-badge ${lead.nachfass.dringend ? 'dringend' : ''}`}>
                        {lead.nachfass.tageUeberfaellig === 0
                          ? 'heute fällig'
                          : `seit ${lead.nachfass.tageUeberfaellig} ${lead.nachfass.tageUeberfaellig === 1 ? 'Tag' : 'Tagen'} überfällig`}
                      </span>
                    )}
                    <span className="lead-date">{formatDate(lead.created_at)}</span>
                  </div>
                  <div className="lead-contact-info">
                    <a href={`tel:${lead.phone}`} className="lead-phone">{lead.phone}</a>
                    {lead.email && <a href={`mailto:${lead.email}`} className="lead-email">{lead.email}</a>}
                    {lead.studio && <span className="lead-studio">{lead.studio}</span>}
                    {lead.source === 'telefon' && <span className="lead-studio lead-source-telefon">Telefon</span>}
                  </div>
                </div>

                {lead.message && (
                  <div className="lead-message">
                    <span className="lead-message-label">Nachricht:</span> {lead.message}
                  </div>
                )}

                <div className="lead-footer">
                  {filterStatus !== 'spam' && zustaendige.length > 0 && (
                    <div className="lead-zuteilung">
                      <select
                        className={lead.assigned_to ? 'zugeteilt' : 'offen'}
                        value={lead.assigned_to || ''}
                        onChange={(e) => handleZuteilung(lead.id, e.target.value)}
                        aria-label="Zuständigkeit"
                      >
                        <option value="">Niemandem zugeteilt</option>
                        {zustaendige.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  )}

                  <div className="lead-status-select">
                    <select
                      value={lead.status}
                      onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                      style={{ borderColor: statusInfo.color }}
                    >
                      {STATUS_OPTIONS.filter(opt => !opt.legacy || opt.value === lead.status).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  {lead.nachfass && lead.nachfass.bereitsKunde && lead.status !== 'converted' && (
                    <button
                      className="leads-btn leads-btn-sm leads-btn-outline"
                      onClick={() => handleStatusChange(lead.id, 'converted')}
                      title="Diese Person ist laut Mitgliederliste bereits Kunde"
                    >
                      Auf „Gewonnen" setzen
                    </button>
                  )}

                  {filterStatus === 'spam' && (
                    <button
                      className="leads-btn leads-btn-sm leads-btn-outline"
                      onClick={() => handleKeinSpam(lead)}
                      title="Anfrage ist echt und soll wieder in der Liste erscheinen"
                    >
                      Kein Spam
                    </button>
                  )}

                  <button
                    className="leads-btn leads-btn-sm leads-btn-danger"
                    onClick={() => handleDelete(lead.id)}
                    title="Anfrage löschen"
                  >
                    Löschen
                  </button>

                  <div className="lead-notes-section">
                    {editingNotes === lead.id ? (
                      <div className="lead-notes-edit">
                        <textarea
                          value={notesText}
                          onChange={(e) => setNotesText(e.target.value)}
                          placeholder="Notizen hinzufügen..."
                          rows="2"
                        />
                        <div className="lead-notes-actions">
                          <button className="leads-btn leads-btn-sm" onClick={() => handleSaveNotes(lead.id)}>Speichern</button>
                          <button className="leads-btn leads-btn-sm leads-btn-outline" onClick={() => setEditingNotes(null)}>Abbrechen</button>
                        </div>
                      </div>
                    ) : (
                      <button
                        className="leads-btn leads-btn-sm leads-btn-outline"
                        onClick={() => { setEditingNotes(lead.id); setNotesText(lead.notes || ''); }}
                      >
                        {lead.notes ? 'Notiz bearbeiten' : 'Notiz hinzufügen'}
                      </button>
                    )}
                    {lead.notes && editingNotes !== lead.id && (
                      <p className="lead-notes-text">{lead.notes}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LeadsManager;
